/**
 * ===========================================
 * StudySpace ERP Frontend - Offline Queue Utility
 * ===========================================
 * 
 * Manages offline payment queue in localStorage.
 * When network is unavailable, payments are queued locally
 * and synced when connection is restored.
 * 
 * Queue Structure:
 * Each item contains: id, type, data, timestamp, attempts
 * 
 * @file src/utils/offlineQueue.js
 * @description Offline payment queue management
 * @task T198
 */

/**
 * Storage key for the offline queue
 * @constant {string}
 */
const QUEUE_KEY = 'studyspace_offline_queue';

/**
 * Maximum retry attempts before marking as failed
 * @constant {number}
 */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Get all items from the offline queue
 * 
 * @returns {Array} Array of queued items
 */
export const getQueue = () => {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to read offline queue:', error);
    return [];
  }
};

/**
 * Save the queue to localStorage
 * 
 * @param {Array} queue - Queue array to save
 */
const saveQueue = (queue) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to save offline queue:', error);
  }
};

/**
 * Generate a unique ID for queue items
 * 
 * @returns {string} Unique identifier
 */
const generateId = () => {
  return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Add an item to the offline queue
 * 
 * @param {string} type - Type of operation ('session_payment', 'cafe_payment')
 * @param {Object} data - Data for the operation
 * @returns {Object} The queued item
 * 
 * @example
 * addToQueue('session_payment', {
 *   sessionId: '123',
 *   amount: 50,
 *   workspaceId: 'ws-1'
 * });
 */
export const addToQueue = (type, data) => {
  const queue = getQueue();

  const item = {
    id: generateId(),
    type,
    data,
    timestamp: new Date().toISOString(),
    attempts: 0,
    status: 'pending' // pending, processing, failed, completed
  };

  queue.push(item);
  saveQueue(queue);

  // Dispatch event for listeners
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { queue } }));

  return item;
};

/**
 * Remove an item from the queue by ID
 * 
 * @param {string} id - Item ID to remove
 * @returns {boolean} True if removed, false if not found
 */
export const removeFromQueue = (id) => {
  const queue = getQueue();
  const index = queue.findIndex(item => item.id === id);

  if (index === -1) return false;

  queue.splice(index, 1);
  saveQueue(queue);

  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { queue } }));

  return true;
};

/**
 * Update an item's status in the queue
 * 
 * @param {string} id - Item ID to update
 * @param {Object} updates - Fields to update
 * @returns {Object|null} Updated item or null if not found
 */
export const updateQueueItem = (id, updates) => {
  const queue = getQueue();
  const index = queue.findIndex(item => item.id === id);

  if (index === -1) return null;

  queue[index] = { ...queue[index], ...updates };
  saveQueue(queue);

  return queue[index];
};

/**
 * Get pending items from the queue
 * 
 * @returns {Array} Array of pending items
 */
export const getPendingItems = () => {
  return getQueue().filter(item =>
    item.status === 'pending' && item.attempts < MAX_RETRY_ATTEMPTS
  );
};

/**
 * Get failed items from the queue
 * 
 * @returns {Array} Array of failed items
 */
export const getFailedItems = () => {
  return getQueue().filter(item =>
    item.status === 'failed' || item.attempts >= MAX_RETRY_ATTEMPTS
  );
};

/**
 * Clear all items from the queue
 * 
 * @returns {void}
 */
export const clearQueue = () => {
  saveQueue([]);
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { queue: [] } }));
};

/**
 * Clear only completed items from the queue
 * 
 * @returns {number} Number of items cleared
 */
export const clearCompleted = () => {
  const queue = getQueue();
  const remaining = queue.filter(item => item.status !== 'completed');
  const cleared = queue.length - remaining.length;

  saveQueue(remaining);
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { detail: { queue: remaining } }));

  return cleared;
};

/**
 * Get queue statistics
 * 
 * @returns {Object} Queue statistics
 */
export const getQueueStats = () => {
  const queue = getQueue();

  return {
    total: queue.length,
    pending: queue.filter(item => item.status === 'pending').length,
    processing: queue.filter(item => item.status === 'processing').length,
    completed: queue.filter(item => item.status === 'completed').length,
    failed: queue.filter(item => item.status === 'failed' || item.attempts >= MAX_RETRY_ATTEMPTS).length
  };
};

/**
 * Check if there are any pending items
 * 
 * @returns {boolean} True if there are pending items
 */
export const hasPendingItems = () => {
  return getPendingItems().length > 0;
};

/**
 * Export queue data for debugging
 * 
 * @returns {Object} Queue data with metadata
 */
export const exportQueueData = () => {
  return {
    exportedAt: new Date().toISOString(),
    stats: getQueueStats(),
    items: getQueue()
  };
};

export default {
  getQueue,
  addToQueue,
  removeFromQueue,
  updateQueueItem,
  getPendingItems,
  getFailedItems,
  clearQueue,
  clearCompleted,
  getQueueStats,
  hasPendingItems,
  exportQueueData,
  MAX_RETRY_ATTEMPTS
};
